import { useLocation } from 'react-router-dom'

import {
  parseQueryParams,
  serializeQueryParams,
} from '~publish/hooks/useQueryParam'
import { insightsChannel, insightsPage } from '~publish/legacy/routes'

import { filterQueryParamsByRoute } from './utils/filterQueryParamsByRoute'

/**
 * Build an Insights URL with the current period selection preserved (date
 * range, custom range dates, comparison) plus tag filters.
 *
 * Insights keeps the selected period in the URL, so a link that drops the
 * search string silently resets the view to the default range. Preserving it
 * lets a customer pick a period once and keep it while moving between the
 * all-channels view and individual channels.
 *
 * When a channelId is provided it returns that channel's Insights URL,
 * otherwise the all-channels view. The channel filter is dropped on the
 * single-channel route, where it has no meaning.
 *
 * @param channelId - Optional channel ID to link to a single channel's Insights
 * @returns URL string with preserved query parameters
 *
 * @example
 * ```tsx
 * // User is on /insights?dateRange=last-month
 * const insightsUrl = useInsightsUrl('channel-123')
 * // Returns: /insights/channel-123?dateRange=last-month
 * ```
 */
export function useInsightsUrl(channelId?: string): string {
  const location = useLocation()
  const currentSearch = parseQueryParams(location.search)

  const route = channelId ? insightsChannel : insightsPage
  const queryString = serializeQueryParams(
    filterQueryParamsByRoute(currentSearch, route.queryParams),
  )
  const pathname = channelId
    ? insightsChannel.buildPathname({ channelId })
    : insightsPage.buildPathname()

  return pathname + (queryString ? `?${queryString}` : '')
}
